import type { ReactNode } from "react";
import { Link } from "react-router-dom";
import Breadcrumbs, { type Crumb } from "@/components/base/Breadcrumbs";

interface PageIntroProps {
  kicker: string;
  title: string;
  description: string;
  breadcrumbs: Crumb[];
  extra?: ReactNode;
}

export default function PageIntro({ kicker, title, description, breadcrumbs, extra }: PageIntroProps) {
  return (
    <section className="border-b border-background-200 bg-background-100">
      <div className="mx-auto w-full max-w-portal px-4 pb-7 pt-5 md:px-6 md:pb-9 md:pt-6">
        <Breadcrumbs items={breadcrumbs} />
        <div className="mt-5 flex flex-col gap-5 md:mt-6 md:flex-row md:items-end md:justify-between">
          <div className="max-w-2xl">
            <p className="flex items-center gap-2 font-label text-[11px] uppercase tracking-[0.24em] text-primary-600">
              <span className="h-3 w-1 rounded-full bg-primary-500" />
              {kicker}
            </p>
            <h1 className="mt-2 font-heading text-2xl font-black tracking-tight text-foreground-950 md:text-[34px]">
              {title}
            </h1>
            <p className="mt-2.5 text-sm leading-relaxed text-foreground-600 md:text-[15px]">{description}</p>
          </div>
          {extra && <div className="shrink-0">{extra}</div>}
        </div>
      </div>
    </section>
  );
}

export function SectionHeading({
  title,
  subtitle,
  actionLabel,
  actionTo,
}: {
  title: string;
  subtitle?: string;
  actionLabel?: string;
  actionTo?: string;
}) {
  return (
    <div className="mb-5 flex items-end justify-between gap-4 border-b border-background-200 pb-3">
      <div>
        <h2 className="flex items-center gap-2 font-heading text-lg font-bold text-foreground-950 md:text-xl">
          <span className="h-4 w-1 rounded-full bg-primary-500" />
          {title}
        </h2>
        {subtitle && <p className="mt-1.5 pl-3 text-xs text-foreground-500 md:text-sm">{subtitle}</p>}
      </div>
      {actionLabel && actionTo && (
        <Link
          to={actionTo}
          className="flex shrink-0 items-center gap-0.5 whitespace-nowrap text-xs text-foreground-500 transition-colors hover:text-primary-600 md:text-sm"
        >
          {actionLabel}
          <i className="ri-arrow-right-s-line" />
        </Link>
      )}
    </div>
  );
}